import PluginPreview from "./PluginPreview/PluginPreview";


const WebViewProps=[
    {
        name:"id",type:"string",
        description:"The webview id",
    },
    {
        name:"file",type:"string",
        description:`
            a filename with extension in your www folder
            @more prioritized than the url prop
        `,
    },
    {
        name:"url",type:"string",
        description:"A http/https url to show external sites",
        notice:`
            Please be careful when using this property because
            the target url will have access to cordova plugins
            @the need for this prop is extremely rare and should not be
            considered as a replacement for 
            <a href="https://github.com/apache/cordova-plugin-inappbrowser">cordova-plugin-inappbrowser</a>
        `,
    },
    {
        name:"statusBarTranslucent",
        type:"boolean",
        defaultValue:"false",
        description:"if True, the status bar will overlay the webview",
    },
    {
        name:"navigationBarTranslucent",
        type:"boolean",
        defaultValue:"false",
        description:`
            if true, the navigation bar will overlay the webview.
            @default to true for modals.
        `,
    },
    {
        name:"statusBarColor",
        type:"string",
        defaultValue:"white",
    },
    {
        name:"backgroundColor",
        type:"string",
        defaultValue:"white",
        description:`
            The webview background color before loading the html file, default to "white".
            @only modals can have a transparent background.
        `,
    },
    {
        name:"navigationBarColor",
        type:"string",
        defaultValue:"white",
    },
    {
        name:"dismissible",
        type:"boolean",
        defaultValue:true,
        description:"if true, the webview is dismissible via touch interactions or back press",
    },
    {
        name:"showAnimation",
        type:`"translateLeft"|"translateUp"|"fadeIn"|"slideLeft"|"slideUp"`,
        defaultValue:null,
        description:`
            The webview animation when shown.
            @Applied only for non-modal webviews.
        `,
    },
    {
        name:"closeAnimation",
        type:`"translateRight"|"translateDown"|"fadeOut"|"slideRight"|"slideDown"`,
        defaultValue:null,
        description:`
            The webview animation when closed.
            @Applied only for non-modal webviews.
        `,
    },
    {
        name:"asModal",
        type:"boolean",
        description:"If true, shows the new webview with a modal animation.",
    },
    {
        name:"modalStyle",
        type:"object",
        description:"Only applied when asModal is true",
        props:[
            {
                name:"width",
                type:"[0..1]",
                defaultValue:1,
                description:"width of the modal relative to the window width",
            },
            {
                name:"height",
                type:"[0..1]",
                defaultValue:0.85,
                description:"height of the modal relative to the window height",
            },
            {
                name:"marginTop",
                type:"[-1..1]",
                defaultValue:0,
                description:"top margin of the modal relative to the window height",
            },
            {
                name:"marginLeft",
                type:"[-1..1]",
                defaultValue:0,
                description:"left margin of the modal relative to the window width",
            },
            {
                name:"verticalAlign",
                type:`"bottom"|"top"|"middle"`,
                defaultValue:"bottom",
            },
            {
                name:"opacity",
                type:"number",
                defaultValue:1,
            },
            {
                name:"silent",
                type:"boolean",
                defaultValue:true,
                description:"if true, the modal pop up sound effect is cancelled",
            },
            {name:"notchColor",type:"string"},
            {
                name:"roundedTopLeftCorner",
                type:"boolean",
                defaultValue:true,
            },
            {
                name:"roundedTopRightCorner",
                type:"boolean",
                defaultValue:true,
            },
            {
                name:"roundedBottomLeftCorner",
                type:"boolean",
                defaultValue:false,
            },
            {
                name:"roundedBottomRightCorner",
                type:"boolean",
                defaultValue:false,
            },
        ],
    },
];

export default {
    id:"WebView",
    name:"vritra-plugin-webview",
    description:`
        Opens a new cordova activity for android, ViewController for ios.
        @the new webview will have access to all cordova plugins
        @defines a global <b>WebView</b> object
    `,
    preview:PluginPreview,
    properties:[
        {
            name:"defineWebViews",
            type:"(webviews:WebViewProps[],fallback:function):void",
            description:"",
            props:[
                {name:"fallback",type:"(message:String):void"},
                {
                    name:"WebViewProps",
                    type:"object",
                    props:WebViewProps,
                }
            ],
        },
        {
            name:"show",
            type:"(options:object):void",
            description:`
                Shows a new webview.
                @The shown webview will have access to all cordova plugins.
                @Overwrites the props defined for the webview with such id 
            `,
            props:[{
                name:"options",type:"object",
                props:[
                    {
                        name:"id",type:"string",
                        description:`
                            The target webview's id. 
                            @The defined webview props are applied as defaults.
                        `,
                    },
                    {
                        name:"message",
                        type:"string",
                        description:`
                            A message to pass to the new webiew.
                            @This message is freed when the new webview is closed
                            and the onClose method is called. 
                            @If message is not of type string, JSON.stringify is called.
                        `,
                    },
                    {
                        name:"onClose",
                        type:"(data:object):void",
                        props:[{
                            name:"data",
                            type:"object",
                            props:[
                                {
                                    name:"message",type:"string",
                                    description:`
                                        A message from the new webview.
                                        @If the new webview did not send a message, this value is the same
                                        as the message passed in the message property when showing the new
                                        webview. 
                                    `,
                                },
                                {
                                    name:"store",
                                    type:"object",
                                    description:"object shared between all webviews",
                                },
                            ],
                        }],
                    },
                    ...WebViewProps.filter(({name})=>name!=="id"),
                ],
            }],
        },
        {
            name:"statusBarColor",
            type:"string",
            description:"changes the webview status bar color",
        },
        {
            name:"navigationBarColor",
            type:"string",
            description:"changes the webview navigation bar color",
        },
        {
            name:"useMessage",
            type:"(handler:function):void",
            description:"Uses the message from the webview that showed the current webview",
            props:[{
                name:"handler",
                type:"(message:string):void",
            }],
        },
        {
            name:"setMessage",
            type:"(message:String):void",
            description:`
                Sets the message value. 
                @if message is a falsy value, it's passed as an empty string.
                @if message is not a string, JSON.stringify is called.
            `,
        },
        {
            name:"initiateStore",
            type:"(store:Object,callback:function):void",
            description:"Sets the store value. Only objects are accepted",
            props:[
                {
                    name:"store",
                    type:"object",
                    description:"store initial value, default to an empty object",
                },
                {
                    name:"callback",
                    type:"(store:Object):void",
                }
            ],
        },
        {
            name:"useStore",
            description:`
                uses the store object.
                @the store object passed to the handler is a value type.
                setting it will not affect the store unless initiateStore is reused
                to overwrite the previous store value or call setStore instead. 
            `,
            overloads:[
                {
                    type:"(callback,fallback):void",
                    description:"gets the whole store object",
                    props:[
                        {
                            name:"callback",
                            type:"(store:object):void",
                        },
                        {
                            name:"fallback",
                            type:"(error:Error):void",
                        },
                    ],
                },
                {
                    type:"(path,callback,fallback):void",
                    description:"gets the value of the store property at path",
                    props:[
                        {
                            name:"path",
                            type:"string",
                            description:"path to the property to get",
                        },
                        {
                            name:"callback",
                            type:"(store:object):void",
                        },
                        {
                            name:"fallback",
                            type:"(error:Error):void",
                        },
                    ],
                },
            ],
        },
        {
            name:"setStore",
            overloads:[
                {
                    type:"(path,value,callback,fallback):void",
                    props:[
                        {
                            name:"path",
                            type:"string",
                            description:`
                                the path to the value you want to set.
                                @ForArrays:
                                @1) array[*] will target all the array items.
                                @2) [index] => sets an array item at index.
                                @3) [last] => uses the last array item.
                                @UsableOnLastPathComponent :
                                @1) [push] => inserts the value at the end of an array.
                                @2) [unshift] => inserts the value at the start of an array.
                                @3) [pop] => removes the last item from an array, value param is ignored.
                                @4) [shift] => removes the first item from an array, value param is ignored.
                                @examples:
                                @"object.array[*][*].object.array[last]"
                                @"object.array[*].object.array[*].property"
                                @"object.array[0].array[4].array[last].property"
                            `,
                        },
                        {name:"value",type:"any"},
                        {
                            name:"callback",
                            type:"(store:object):void",
                        },
                        {
                            name:"fallback",
                            type:"(error:Error):void",
                        },
                    ],
                },
                {
                    type:"(entries,callback,fallback):void",
                    description:`
                        Multi sets the store
                        @deletes the value if undefined is used
                    `,
                    props:[
                        {
                            name:"entries",
                            type:"[string,any,...]",
                            description:"should contain string-any successive items",
                        },
                        {
                            name:"callback",
                            type:"(store:object):void",
                        },
                        {
                            name:"fallback",
                            type:"(error:Error):void",
                        },
                    ],
                },
                {
                    type:"(path,callback,fallback):void",
                    description:"deletes value at path",
                    props:[
                        {
                            name:"path",
                            type:"string",
                        },
                        {
                            name:"callback",
                            type:"(store:object):void",
                        },
                        {
                            name:"fallback",
                            type:"(error:Error):void",
                        },
                    ],
                },
            ],
        },
        {
            name:"close",
            type:"(message:String):void",
            description:"Closes the current webview",
            props:[{
                name:"message",
                type:"string",
                description:`
                    The message to pass to the previous webview.
                    @if message is undefined, the value is ignored.
                    @if message is not a string, JSON.stringify is called.
                `,
            }],
            notice:`if called in the main webview, the app is minimized.`,
        },
    ],
}
